import Rendable from "@/glxp/abstract/rendable"
import Shader from "@/glxp/utils/shader"
import Node from "@/glxp/abstract/node"
import DebugController from '@/glxp/debug/debugController'


import GEOM from "@/glxp/geom/plane"
import ShaderManifest from "../shaderManifest"
import { vec2, vec3, mat4 } from 'gl-matrix'

let _NV = mat4.create()

function hexToRgb(hex) {
  var result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? [
    parseInt(result[1], 16)/255,
    parseInt(result[2], 16)/255,
    parseInt(result[3], 16)/255
   ] : null;
}

class Plateform extends Rendable {
  constructor(scene, geom, shader, texture) {
    super(scene)

    this.gl = scene.gl
    this.scene = scene
    this.geom = geom
    this.name = geom.name

    this.node = new Node()
    this.shader = new Shader(ShaderManifest[shader])
    this.texture = texture
    this.active = true
    this.depthTest = true
    this.depthWrite = true
    this.side = "both"
    this.mask = 0
    this.progress = 0
    this.colorSet = 0

    this.node.position  = vec3.clone(geom.translate)
    this.node.scale     = vec3.clone(geom.scale)
    this.node.rotation  = vec3.clone(geom.rotation)
    this.opacity = 1

    this.isTopPlateform = this.name.includes("Top_Rock_Plateform")

    this.hasNormal = true
    this.initBuffer({
      vertices: geom.vertices,
      uvs: geom.uvs,
      indices: geom.indices,
      normal: geom.normal,
    })

    this.initProgram(this.shader.vert, this.shader.frag)
    this.initVao()
    this.initMatrix()
    this.createUniforms()

  }

  createUniforms() {
    this.createUniform('uNMatrix', 'mat4')
    this.createUniform("uTexture", "texture")
    this.createUniform("uGrain", "texture")
    this.createUniform("uNoise", "texture")
    this.createUniform("uRefect", "texture")
    this.createUniform("uOpacity")
    this.createUniform("uProgress")
    this.createUniform("uGrainValue")
    this.createUniform("uGrainScale")
    this.createUniform("uTopPlateform")
    this.createUniform("uGradientRotation")
    this.createUniform("uColorSet")
    this.createUniform("uColor1", "float3")
    this.createUniform("uColor2", "float3")
    this.createUniform("uColor3", "float3")
    this.createUniform("uColor4", "float3")
  }

  applyState() {
    let gl = this.gl
    this.scene.applyDefaultState()
    if (this.depthTest == false) {
      gl.disable(gl.DEPTH_TEST)
    }
    gl.depthMask(this.depthWrite)
    if (this.side == "front") {
      gl.enable(gl.CULL_FACE);
      gl.cullFace(gl.FRONT);
    } else if (this.side == "back"){
      gl.enable(gl.CULL_FACE);
      gl.cullFace(gl.BACK);
    }

  }

  preRender() {}

  render(mask = 0) {
    if (!this.active || this.mask !== mask) {
      return
    }
    this.preRender()

    let gl = this.gl
    gl.useProgram(this.program)
    gl.bindVertexArray(this.vao)
    this.applyState()

    mat4.mul(_NV, this.scene.camera.getViewMatrix(), this.node.getMatrix())
    mat4.invert(_NV, _NV)
    mat4.transpose(_NV, _NV)
    this.bindUniform('uNMatrix', _NV)

    if (this.texture) {
      gl.activeTexture(gl.TEXTURE0)
      gl.bindTexture(
        gl.TEXTURE_2D,
        this.scene.textureLoader.getTexture(this.texture)
      )
      this.bindUniform("uTexture", 0)
    }

    gl.activeTexture(gl.TEXTURE1)
    gl.bindTexture(
      gl.TEXTURE_2D,
      this.scene.textureLoader.getTexture("Grain")
    )
    this.bindUniform('uGrain', 1)

    gl.activeTexture(gl.TEXTURE2)
    gl.bindTexture(
      gl.TEXTURE_2D,
      this.scene.textureLoader.getTexture("Voronoi")
    )
    this.bindUniform('uNoise', 2)

    gl.activeTexture(gl.TEXTURE3)
    this.scene.handsReflectRT.bind()
    this.bindUniform('uRefect', 3)

  
    this.bindUniform('uOpacity', this.opacity * (this.isTopPlateform ? this.scene.plateformConfig.opacityTop.value : this.scene.plateformConfig.opacityBottom.value))
    this.bindUniform('uProgress', this.progress)
    this.bindUniform('uTopPlateform', this.isTopPlateform ? 1 : 0)
    this.bindUniform('uColorSet', this.colorSet)

    this.bindUniform('uGrainValue', this.scene.plateformConfig.grain.value)
    this.bindUniform('uGrainScale', this.scene.plateformConfig.grainScale.value)
    
    this.bindUniform('uGradientRotation', this.scene.plateformConfig.gradientRotation.value)

    this.bindUniform('uColor1', [this.scene.plateformConfig.color1_Step1.value[0] / 255, this.scene.plateformConfig.color1_Step1.value[1] / 255, this.scene.plateformConfig.color1_Step1.value[2] / 255,])
    this.bindUniform('uColor2', [this.scene.plateformConfig.color2_Step1.value[0] / 255, this.scene.plateformConfig.color2_Step1.value[1] / 255, this.scene.plateformConfig.color2_Step1.value[2] / 255,])
    this.bindUniform('uColor3', [this.scene.plateformConfig.color1_Step2.value[0] / 255, this.scene.plateformConfig.color1_Step2.value[1] / 255, this.scene.plateformConfig.color1_Step2.value[2] / 255,])
    this.bindUniform('uColor4', [this.scene.plateformConfig.color2_Step2.value[0] / 255, this.scene.plateformConfig.color2_Step2.value[1] / 255, this.scene.plateformConfig.color2_Step2.value[2] / 255,])

    this.bindMatrixUniforms(this.scene.camera)

    gl.drawElements(
      gl.TRIANGLES,
      this.geom.indices.length,
      gl.UNSIGNED_SHORT,
      0
    )
    gl.bindVertexArray(null)
  }
}

export default Plateform
